import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { can, signInURL, type Chapter, type Me } from "../api";
import { useT } from "../i18n";
import { primaryOrg, useSignOut } from "../session";
import { Avatar } from "./AccountMenu";
import Logo from "./Logo";
import MutationError from "./MutationError";
import { BackIcon, CloseIcon } from "./icons";

/**
 * ReaderDrawer is everything the reader page does not keep on screen: the way
 * back, the rest of the app, settings, and the account.
 *
 * It replaces the app header on the reader route. Everything the header held is
 * here, so hiding the header takes nothing away — it moves one tap further from
 * a page where none of it is used mid-chapter.
 *
 * Settings are a link, not controls: with text size, themes and language inline
 * the drawer opened on a wall of buttons when all most people wanted was the
 * contents. They are a page of their own (/settings).
 *
 * It slides in from the interface's starting edge, where its button is: the
 * left in English, the right in Persian (ADR-0023).
 */
export default function ReaderDrawer({
  me,
  bookID,
  chapterID,
  chapters = [],
  studioChapterID,
  onClose,
}: {
  me: Me | null;
  bookID?: string;
  /** The chapter open behind the drawer, marked in the list. */
  chapterID?: string;
  /** The book's chapters as readers have them, in the book's order. */
  chapters?: Chapter[];
  /** Set when this account may edit the chapter. */
  studioChapterID?: string;
  onClose: () => void;
}) {
  const close = useRef<HTMLButtonElement>(null);
  const signOut = useSignOut();
  const org = primaryOrg(me);
  const t = useT();

  // A long book's list opens on the chapter being read, not on chapter one.
  // Once, when the list is there: a later render must not pull it back.
  const current = useRef<HTMLAnchorElement>(null);
  const listed = chapters.length > 0;
  useEffect(() => {
    current.current?.scrollIntoView({ block: "center" });
  }, [listed]);

  // Escape closes, focus starts inside, and the page behind does not scroll
  // under a finger dragging on the drawer.
  useEffect(() => {
    close.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  const item =
    "block rounded-control px-3 py-2.5 text-sm text-ink-soft transition-colors hover:bg-sunk hover:text-ink";

  return (
    <div className="fixed inset-0 z-50">
      <div
        data-testid="reader-drawer-backdrop"
        className="animate-fade-in absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t.drawer.menu}
        data-testid="reader-drawer"
        className="animate-drawer-in absolute inset-y-0 start-0 flex w-[min(20rem,85vw)] flex-col overflow-y-auto border-e border-line bg-surface pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex items-center justify-between py-2 ps-4 pe-2">
          <Link
            to="/"
            onClick={onClose}
            className="flex items-center gap-2 rounded-control"
            aria-label={t.nav.home}
          >
            <Logo size={24} />
            <span className="text-[0.95rem] font-semibold tracking-tight">{t.nav.brand}</span>
          </Link>
          <button
            ref={close}
            type="button"
            aria-label={t.drawer.closeMenu}
            data-testid="reader-drawer-close"
            onClick={onClose}
            className="grid size-10 place-items-center rounded-full text-muted transition-colors hover:bg-sunk hover:text-ink"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="border-t border-line px-2 py-2">
          <Link
            to={bookID ? `/books/${bookID}` : "/"}
            onClick={onClose}
            data-testid="drawer-contents"
            className="flex items-center gap-2 rounded-control px-3 py-2.5 text-sm text-ink-soft transition-colors hover:bg-sunk hover:text-ink"
          >
            <BackIcon className="size-4 shrink-0" />
            {t.drawer.contents}
          </Link>
          <Link to="/" onClick={onClose} data-testid="drawer-library" className={item}>
            {t.nav.library}
          </Link>
          {me && can(org, "book.read_draft") && (
            <Link to="/studio" onClick={onClose} className={item}>
              {t.nav.studio}
            </Link>
          )}
          {studioChapterID && (
            <Link
              to={`/studio/chapters/${studioChapterID}`}
              onClick={onClose}
              data-testid="open-in-studio"
              className={item}
            >
              {t.drawer.editChapter}
            </Link>
          )}
          <Link to="/settings" onClick={onClose} data-testid="drawer-settings" className={item}>
            {t.nav.settings}
          </Link>
        </nav>

        {chapters.length > 0 && (
          <nav
            aria-label={t.drawer.chapters}
            data-testid="drawer-chapters"
            className="border-t border-line px-2 py-2"
          >
            <h2 className="px-3 pt-1 pb-1.5 text-xs font-medium text-muted">{t.drawer.chapters}</h2>
            <ol>
              {chapters.map((chapter, i) => {
                const here = chapter.id === chapterID;
                return (
                  <li key={chapter.id}>
                    <Link
                      ref={here ? current : undefined}
                      to={`/read/${chapter.id}`}
                      onClick={onClose}
                      aria-current={here ? "page" : undefined}
                      data-testid="drawer-chapter"
                      className={`flex items-baseline gap-2.5 rounded-control px-3 py-2 text-sm transition-colors hover:bg-sunk ${
                        here ? "bg-sage-soft font-medium text-ink" : "text-ink-soft hover:text-ink"
                      }`}
                    >
                      <span className="tabular w-5 shrink-0 text-xs text-muted">
                        {t.meta.num(i + 1)}
                      </span>
                      {/* The title alone carries dir="auto"; the number is the
                          interface's and sits on its starting side. */}
                      <span dir="auto" className="min-w-0 flex-1 truncate">
                        {chapter.title}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </nav>
        )}

        <div className="mt-auto border-t border-line px-2 py-2">
          {me ? (
            <>
              <div className="flex items-center gap-3 px-3 py-2">
                <Avatar
                  name={me.user.name || me.user.email}
                  imageURL={me.user.image_url}
                  size={32}
                />
                <div className="min-w-0">
                  {me.user.name && (
                    <p dir="auto" className="truncate text-sm font-medium">
                      {me.user.name}
                    </p>
                  )}
                  <p className="truncate text-xs text-muted">{me.user.email}</p>
                </div>
              </div>
              <Link to="/usage" onClick={onClose} className={item}>
                {t.nav.usage}
              </Link>
              {me.user.platform_admin && (
                <Link to="/admin" onClick={onClose} className={item}>
                  {t.nav.admin}
                </Link>
              )}
              <button
                type="button"
                data-testid="drawer-sign-out"
                disabled={signOut.isPending}
                // Closed only once the session has ended, so a refusal is
                // said where it happened.
                onClick={() => signOut.mutate(undefined, { onSuccess: onClose })}
                className={`${item} w-full text-start disabled:opacity-50`}
              >
                {t.nav.signOut}
              </button>
              <MutationError
                error={signOut.error}
                fallback={t.nav.signOutFailed}
                testId="drawer-sign-out-error"
                className="px-3 py-2"
              />
            </>
          ) : (
            <a href={signInURL()} data-testid="drawer-sign-in" className={item}>
              {t.nav.signIn}
            </a>
          )}
        </div>
      </aside>
    </div>
  );
}
